import { Button, Stack, Typography } from '@mui/material'
import { forwardRef, useState } from 'react'
import {
  IconButton,
  InfoModal,
  MaskedInputField,
  MaskedInputFieldProps,
  TextInputField,
  TextInputFieldProps,
  TextLink,
} from 'utoniq-ui'

import {
  Form,
  handleError,
  tLabel,
  useForm,
  UseFormProps,
  useMessage,
  useTranslation,
} from '@/core'
import { CellFrame, Organism } from '@/core/architecture'

export type BankEditFormProps = {
  onSubmit: (values: BankValues) => Promise<void>
}

export type BankValues = {
  bankCode: string
  branchCode: string
  accountNumber: string
  accountHolderName: string
}

const schema: UseFormProps['schema'] = o => ({
  bankCode: o
    .string()
    .regex(/^[0-9]{4}$/)
    .label(tLabel('consoleBankEdit.bankCode')),
  branchCode: o
    .string()
    .regex(/^[0-9]{3}$/)
    .label(tLabel('consoleBankEdit.branchCode')),
  accountNumber: o
    .string()
    .regex(/^[0-9]{7}$/)
    .label(tLabel('consoleBankEdit.accountNumber')),
  accountHolderName: o
    .string()
    .label(tLabel('consoleBankEdit.accountHolderName')),
})

export const BankEditForm: Organism<BankEditFormProps> = ({ onSubmit }) => {
  const { t } = useTranslation('consoleBankEdit')
  const { showMessage, showError } = useMessage()
  const [openBankCode, setOpenBankCode] = useState<boolean>(false)
  const [openBranchCode, setOpenBranchCode] = useState<boolean>(false)
  const {
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
    register,
    reset,
    setError,
  } = useForm<BankValues>({
    schema,
    defaultValues,
  })

  const handleOnSubmit = handleSubmit(async values => {
    await onSubmit(values)
      .then(() => {
        reset(defaultValues)
        showMessage(t('registerSuccess'), 'success')
      })
      .catch(error => handleError<BankValues>(error, setError, showError))
  })

  return (
    <Form
      onSubmit={handleOnSubmit}
      sx={{ px: 5, bgcolor: 'background.default' }}
    >
      <MaskedInputCell
        type="text"
        mask="0000"
        titleProps={{
          children: (
            <>
              {t('bankCodeTitle')}
              <IconButton.Help
                size="small"
                onClick={() => setOpenBankCode(true)}
                color="primary.main"
              />
            </>
          ),
        }}
        error={errors?.bankCode}
        {...register('bankCode')}
      />
      <MaskedInputCell
        type="text"
        mask="000"
        titleProps={{
          children: (
            <>
              {t('branchCodeTitle')}
              <IconButton.Help
                size="small"
                onClick={() => setOpenBranchCode(true)}
                color="primary.main"
              />
            </>
          ),
        }}
        error={errors?.branchCode}
        {...register('branchCode')}
      />
      <MaskedInputCell
        type="text"
        mask="0000000"
        error={errors?.accountNumber}
        {...register('accountNumber')}
      />
      <TextInputCell
        type="text"
        error={errors?.accountHolderName}
        {...register('accountHolderName')}
      />
      <Stack alignItems="center" spacing={4} sx={{ pb: 6 }}>
        <Button
          loading={isSubmitting}
          type="submit"
          size="large"
          sx={{ width: 244 }}
          disabled={!isValid || isSubmitting}
        >
          {t('register')}
        </Button>
        <TextLink
          variant="subtitle1"
          color="primary"
          href="https://about.utoniq.com/#contact"
          target="_blank"
          component="a"
          rel="noopener noreferrer"
        >
          {t('clickHere')}
        </TextLink>
      </Stack>
      <InfoModal
        type="help"
        open={openBankCode}
        onClose={() => setOpenBankCode(false)}
        title={t('bankCodeModalTitle')}
        content={
          <Stack spacing={4}>
            <Typography variant="body2">{t('bankCodeModalDesc')}</Typography>
            <TextLink
              variant="caption"
              color="primary"
              href="https://zengin.ajtw.net/"
              target="_blank"
              component="a"
              rel="noopener noreferrer"
            >
              {t('linkToOtherBank')}
            </TextLink>
          </Stack>
        }
      />
      <InfoModal
        type="help"
        open={openBranchCode}
        onClose={() => setOpenBranchCode(false)}
        title={t('branchCodeModalTitle')}
        content={
          <Typography variant="body2">{t('branchCodeModalDesc')}</Typography>
        }
      />
    </Form>
  )
}

const MaskedInputCell = forwardRef(
  (
    {
      titleProps,
      textFieldProps,
      error,
      ...props
    }: Omit<MaskedInputFieldProps, 't'>,
    ref
  ) => {
    const { t } = useTranslation('consoleBankEdit')
    return (
      <CellFrame>
        <MaskedInputField
          {...props}
          titleProps={{
            variant: 'h5',
            color: 'text.primary',
            sx: { alignItems: 'center', display: 'flex' },
            ...titleProps,
          }}
          textFieldProps={textFieldProps}
          ref={ref}
          t={t}
          error={error}
          placeholder={t(`${props.name}Placeholder`)}
        />
      </CellFrame>
    )
  }
)

const TextInputCell = forwardRef(
  (
    {
      titleProps,
      textFieldProps,
      error,
      ...props
    }: Omit<TextInputFieldProps, 't'>,
    ref
  ) => {
    const { t } = useTranslation('consoleBankEdit')
    return (
      <CellFrame>
        <TextInputField
          {...props}
          titleProps={{
            variant: 'h5',
            color: 'text.primary',
            sx: { alignItems: 'center', display: 'flex' },
            ...titleProps,
          }}
          textFieldProps={textFieldProps}
          error={error}
          ref={ref}
          t={t}
        />
      </CellFrame>
    )
  }
)

const defaultValues: BankValues = {
  bankCode: '',
  branchCode: '',
  accountNumber: '',
  accountHolderName: '',
}
